import { type PreparedShape, type Rng, type ShapeDefinition, type ShapeSink } from '../shape.js';
import { bucket, hash01, inBox, onDisc, ramp, revolve, TAU } from './helpers.js';

/* ── City ──────────────────────────────────────────────────────────────── */

interface Tower {
  readonly x: number;
  readonly z: number;
  readonly width: number;
  readonly depth: number;
  readonly height: number;
}

function buildTowers(random: Rng, variant: number): Tower[] {
  const towers: Tower[] = [];
  const span = 9;
  const cell = 2.6;
  for (let gx = -span; gx <= span; gx += 1) {
    for (let gz = -span; gz <= span; gz += 1) {
      const distance = Math.hypot(gx, gz) / span;
      // Thinning the grid towards the edges gives the city an outskirts.
      if (random() >= distance * 0.55) {
        // Height falls off from the centre, so the skyline has a downtown.
        // `distance` passes 1 at the corners of the grid, so clamp before the
        // fractional power — a negative base would be NaN.
        const tall = Math.max(0, 1 - distance) ** 1.6 * (14 + variant * 12) + 1.2 + random() * 3.5;
        towers.push({
          x: gx * cell + (random() - 0.5) * 0.5,
          z: gz * cell + (random() - 0.5) * 0.5,
          width: 0.7 + random() * 0.85,
          depth: 0.7 + random() * 0.85,
          height: tall,
        });
      }
    }
  }
  return towers;
}

/** Place a particle on a tower facade, snapped to that facade's window grid. */
function onFacade(sink: ShapeSink, random: Rng, tower: Tower, id: number): void {
  const face = bucket(random, 4);
  const columns = Math.max(2, Math.round(tower.width / 0.3));
  const rows = Math.max(3, Math.round(tower.height / 0.62));
  const column = Math.floor(random() * columns);
  const row = Math.floor(random() * rows);
  const u = ((column + 0.5) / columns - 0.5) * 2;
  const v = (row + 0.4) / rows;
  const facingSide = face % 2 === 0 ? 1 : -1;
  const isBroadFace = face < 2;
  const acrossX = isBroadFace ? tower.width * u : tower.width * facingSide;
  const acrossZ = isBroadFace ? tower.depth * facingSide : tower.depth * u;
  sink.x = tower.x + acrossX + (random() - 0.5) * 0.12;
  sink.z = tower.z + acrossZ + (random() - 0.5) * 0.12;
  sink.y = v * tower.height + (random() - 0.5) * 0.16;
  const isLit = hash01(id * 131 + column * 17 + row * 7) < 0.26;
  sink.tint = isLit ? 0.88 + random() * 0.12 : 0.04 + random() * 0.16;
}

export const city: ShapeDefinition = {
  name: 'city',
  palette: { base: 0x3a_44_68, shade: 0x84_94_c0, accent: 0xff_d2_8a },
  driftRate: 0.2,
  framing: 2.03,
  prepare: ({ random, total, variant }): PreparedShape => {
    const towers = buildTowers(random, variant);
    const count = towers.length;
    const place = (index: number, sink: ShapeSink): void => {
      const p = index / total;
      if (p < 0.76) {
        const pick = Math.floor(random() * count);
        const tower = towers[pick];
        if (tower !== undefined) {
          onFacade(sink, random, tower, pick);
        }
      } else if (p < 0.9) {
        // Streets: a dark plane threaded with warm lamp light.
        onDisc(sink, random, 26);
        const lane = Math.abs(Math.sin(sink.x * 1.2) * Math.sin(sink.z * 1.2));
        sink.y = 0.02;
        sink.tint = lane > 0.93 ? 0.8 : 0.02 + lane * 0.1;
      } else {
        // Sodium haze hanging over the whole grid.
        onDisc(sink, random, 30);
        sink.y = 0.5 + random() * 9;
        sink.tint = 0.45 + random() * 0.2;
        sink.driftX = 0.4;
        sink.driftZ = 0.4;
        sink.phase = hash01(index) * TAU;
      }
    };
    return { place };
  },
};

/* ── Cathedral ─────────────────────────────────────────────────────────── */

const NAVE_HALF = 4.2;
const NAVE_LENGTH = 26;
const COLUMN_HEIGHT = 9;

function vaultPoint(sink: ShapeSink, random: Rng, z: number): void {
  // A semicircular barrel vault springing from the top of the columns.
  const angle = random() * Math.PI;
  sink.x = Math.cos(angle) * NAVE_HALF;
  sink.y = COLUMN_HEIGHT + Math.sin(angle) * NAVE_HALF * 1.15;
  sink.z = z;
}

export const cathedral: ShapeDefinition = {
  name: 'cathedral',
  palette: { base: 0x4a_44_58, shade: 0xa8_9c_88, accent: 0xff_c9_5e },
  driftRate: 0.25,
  framing: 1.82,
  prepare: ({ random, total, variant }): PreparedShape => {
    const bays = 6 + Math.floor(variant * 4);
    const spacing = NAVE_LENGTH / bays;
    const columnZ = (i: number): number => -NAVE_LENGTH / 2 + (i + 0.5) * spacing;
    const place = (index: number, sink: ShapeSink): void => {
      const p = index / total;
      if (p < 0.34) {
        // Fluted columns down both sides of the nave.
        const bay = Math.floor(random() * bays);
        const side = random() < 0.5 ? -1 : 1;
        revolve(
          sink,
          random,
          (v) => 0.42 + (v > 0.93 ? 0.28 : 0) + (v < 0.05 ? 0.2 : 0),
          COLUMN_HEIGHT,
        );
        sink.x += side * NAVE_HALF;
        sink.z += columnZ(bay);
        sink.tint = 0.35 + random() * 0.18;
      } else if (p < 0.52) {
        // Arcade: round arches linking neighbouring columns.
        const bay = Math.floor(random() * (bays - 1));
        const side = random() < 0.5 ? -1 : 1;
        const angle = random() * Math.PI;
        const mid = (columnZ(bay) + columnZ(bay + 1)) / 2;
        sink.x = side * NAVE_HALF + (random() - 0.5) * 0.35;
        sink.y = COLUMN_HEIGHT * 0.72 + Math.sin(angle) * spacing * 0.5;
        sink.z = mid - Math.cos(angle) * spacing * 0.5;
        sink.tint = 0.45 + random() * 0.15;
      } else if (p < 0.74) {
        vaultPoint(sink, random, (random() - 0.5) * NAVE_LENGTH);
        sink.tint = 0.28 + random() * 0.2;
      } else if (p < 0.88) {
        // Flagstone floor.
        sink.x = (random() - 0.5) * NAVE_HALF * 2.4;
        sink.y = 0;
        sink.z = (random() - 0.5) * NAVE_LENGTH;
        sink.tint = 0.16 + random() * 0.1;
      } else if (p < 0.955) {
        // The rose window: radial tracery, lit from behind.
        const spokes = 12;
        const r = Math.sqrt(random()) * 3.2;
        const angle = random() * TAU;
        const rib = Math.abs(Math.sin(angle * spokes * 0.5));
        sink.x = Math.cos(angle) * r;
        sink.y = COLUMN_HEIGHT + 1.4 + Math.sin(angle) * r;
        sink.z = -NAVE_LENGTH / 2 - 0.6;
        sink.tint = rib < 0.25 ? 0.3 : 0.72 + random() * 0.16;
      } else {
        // Candlelight and dust in the shafts.
        sink.x = (random() - 0.5) * NAVE_HALF * 2;
        sink.y = random() * COLUMN_HEIGHT;
        sink.z = (random() - 0.5) * NAVE_LENGTH;
        sink.tint = 0.95;
        sink.driftY = 0.4;
        sink.driftX = 0.2;
        sink.phase = hash01(index) * TAU;
      }
    };
    return { place };
  },
};

/* ── Bridge ────────────────────────────────────────────────────────────── */

const SPAN = 13;
const DECK_Y = 5.5;
const TOWER_TOP = 15;

function catenary(x: number): number {
  const t = Math.abs(x) / SPAN;
  return DECK_Y + (TOWER_TOP - DECK_Y) * t * t;
}

/** Roadway deck and its side railings. */
function bridgeDeck(sink: ShapeSink, random: Rng): void {
  sink.x = (random() - 0.5) * SPAN * 2.6;
  sink.z = (random() - 0.5) * 3.2;
  const isRail = Math.abs(sink.z) > 1.4;
  sink.y = DECK_Y + (isRail ? random() * 0.7 : 0);
  sink.tint = isRail ? 0.55 : 0.1 + random() * 0.12;
}

/** A tower: two legs with cross-braces between them. */
function bridgeTower(sink: ShapeSink, random: Rng): void {
  const side = random() < 0.5 ? -1 : 1;
  const leg = random() < 0.5 ? -1 : 1;
  inBox(sink, random, { x: 0.32, y: TOWER_TOP / 2, z: 0.32 });
  sink.x += side * SPAN;
  sink.y += TOWER_TOP / 2;
  sink.z += leg * 1.7;
  const isBrace = Math.abs(Math.sin(sink.y * 0.55)) > 0.96;
  sink.tint = isBrace ? 0.7 : 0.42 + random() * 0.14;
}

/** The river below, catching the deck lights. */
function bridgeWater(sink: ShapeSink, random: Rng): void {
  sink.x = (random() - 0.5) * 46;
  sink.z = (random() - 0.5) * 30;
  sink.y = Math.sin(sink.x * 0.7 + sink.z * 0.3) * 0.08;
  const isGlint = Math.abs(Math.sin(sink.x * 2.1)) > 0.9 && Math.abs(sink.x) < SPAN * 1.4;
  sink.tint = isGlint ? 0.9 : 0.02 + random() * 0.08;
  sink.driftY = 0.14;
  sink.phase = sink.x * 0.6;
}

function placeBridge(sink: ShapeSink, random: Rng, hangers: number, p: number): void {
  if (p < 0.24) {
    bridgeDeck(sink, random);
  } else if (p < 0.44) {
    bridgeTower(sink, random);
  } else if (p < 0.62) {
    // Main cables sweeping from tower top to deck.
    const x = (random() * 2 - 1) * SPAN;
    sink.x = x;
    sink.y = catenary(x) + (random() - 0.5) * 0.14;
    sink.z = (random() < 0.5 ? -1 : 1) * 1.7;
    sink.tint = 0.62 + random() * 0.14;
  } else if (p < 0.76) {
    // Vertical hangers, evenly spaced along both cables.
    const x = (Math.floor(random() * hangers) / (hangers - 1) - 0.5) * 2 * SPAN;
    sink.x = x + (random() - 0.5) * 0.06;
    sink.y = DECK_Y + random() * (catenary(x) - DECK_Y);
    sink.z = (random() < 0.5 ? -1 : 1) * 1.7;
    sink.tint = 0.5;
  } else if (p < 0.9) {
    bridgeWater(sink, random);
  } else {
    // Lamp strings along the deck.
    const slot = Math.floor(random() * hangers);
    sink.x = (slot / (hangers - 1) - 0.5) * 2 * SPAN * 1.25;
    sink.y = DECK_Y + 1.5;
    sink.z = (random() < 0.5 ? -1 : 1) * 1.6;
    sink.tint = 0.96;
  }
}

export const bridge: ShapeDefinition = {
  name: 'bridge',
  palette: { base: 0x6a_32_30, shade: 0xd0_68_4a, accent: 0xff_e2_a8 },
  driftRate: 0.6,
  framing: 1.89,
  prepare: ({ random, total, variant }): PreparedShape => {
    const hangers = 22 + Math.floor(variant * 14);
    return {
      place: (index: number, sink: ShapeSink): void => {
        placeBridge(sink, random, hangers, index / total);
      },
    };
  },
};

/* ── Spiral staircase ──────────────────────────────────────────────────── */

interface Helix {
  readonly treads: number;
  readonly rise: number;
  readonly sweep: number;
  readonly inner: number;
  readonly outer: number;
}

/** Wedge-shaped treads winding upwards around the newel. */
function stairTread(sink: ShapeSink, random: Rng, helix: Helix, step: number): void {
  const r = helix.inner + Math.sqrt(random()) * (helix.outer - helix.inner);
  const spread = (random() - 0.5) * helix.sweep * 0.86;
  const angle = step * helix.sweep + spread;
  sink.x = Math.cos(angle) * r;
  sink.z = Math.sin(angle) * r;
  sink.y = step * helix.rise;
  sink.tint = 0.3 + ramp(r, helix.inner, helix.outer) * 0.3 + random() * 0.06;
}

/** The banister helix and the balusters holding it up. */
function stairRail(sink: ShapeSink, random: Rng, helix: Helix): void {
  const t = random();
  const angle = t * helix.treads * helix.sweep;
  const isRail = random() < 0.55;
  const top = t * helix.treads * helix.rise;
  sink.x = Math.cos(angle) * helix.outer;
  sink.z = Math.sin(angle) * helix.outer;
  sink.y = top + (isRail ? 0.95 : random() * 0.95);
  sink.tint = isRail ? 0.62 : 0.44;
}

function placeStairs(sink: ShapeSink, random: Rng, helix: Helix, p: number): void {
  const step = Math.floor(random() * helix.treads);
  if (p < 0.56) {
    stairTread(sink, random, helix, step);
  } else if (p < 0.72) {
    revolve(sink, random, () => helix.inner * (0.5 + random() * 0.5), helix.treads * helix.rise);
    sink.tint = 0.2 + random() * 0.1;
  } else if (p < 0.88) {
    stairRail(sink, random, helix);
  } else {
    // Light falling in from a window far above.
    const angle = random() * TAU;
    const r = random() * helix.outer * 1.3;
    sink.x = Math.cos(angle) * r;
    sink.z = Math.sin(angle) * r;
    sink.y = random() * helix.treads * helix.rise;
    sink.tint = 0.92 + random() * 0.08;
    sink.driftY = 0.5;
    sink.phase = hash01(Math.floor(r * 977)) * TAU;
  }
}

export const stairs: ShapeDefinition = {
  name: 'stairs',
  palette: { base: 0x54_40_5c, shade: 0xb0_8c_68, accent: 0xff_e9_c4 },
  driftRate: 0.4,
  framing: 2.43,
  prepare: ({ random, total, variant }): PreparedShape => {
    const helix: Helix = {
      treads: 54 + Math.floor(variant * 40),
      rise: 0.32,
      sweep: 0.42,
      inner: 0.55,
      outer: 4.3,
    };
    return {
      place: (index: number, sink: ShapeSink): void => {
        placeStairs(sink, random, helix, index / total);
      },
    };
  },
};
